/**
 * Belegvorlagen: das anpassbare Layout des Beleg-PDFs.
 *
 * Eine Vorlage beschreibt, wo die Bloecke eines Belegs stehen und wie sie
 * aussehen. Der entscheidende Schutz liegt im Modell selbst: Die
 * Pflichtbloecke (Anschriftfeld, Infoblock mit Belegnummer und Datum,
 * Positionstabelle, Summen, Fusszeile nach § 35a GmbHG) sind keine
 * optionalen Eintraege, sondern feste Bestandteile der Vorlage. Sie lassen
 * sich verschieben und umgestalten, aber nicht entfernen - eine Vorlage
 * ohne Steuernummer kann gar nicht erst entstehen.
 *
 * Masse stehen in Millimetern auf einer A4-Seite (210 x 297), Schriften in
 * Punkt. Die Umrechnung in PDF-Punkte uebernimmt der Renderer.
 */
import { z } from 'zod';
const hexColor = z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Farben werden als Hex-Wert angegeben, z. B. #1a2b3c.');
const xMm = z
    .number()
    .min(0, 'Die X-Position kann nicht links neben der Seite liegen.')
    .max(200, 'Die X-Position liegt rechts neben der Seite (max. 200 mm).');
const yMm = z
    .number()
    .min(0, 'Die Y-Position kann nicht über der Seite liegen.')
    .max(285, 'Die Y-Position liegt unter der Seite (max. 285 mm).');
const marginMm = z
    .number()
    .min(5, 'Ein Seitenrand unter 5 mm wird von vielen Druckern abgeschnitten.')
    .max(40, 'Ein Seitenrand über 40 mm lässt zu wenig Platz für den Inhalt.');
const align = z.enum(['left', 'center', 'right']);
/** Frei platzierbarer Textbaustein, z. B. ein Hinweis oder eine Zertifikatsnummer. */
const extraTextSchema = z.object({
    /** Text mit Platzhaltern in geschweiften Klammern, siehe TEMPLATE_FIELDS. */
    text: z.string().min(1, 'Der Text darf nicht leer sein.').max(500),
    xMm,
    yMm,
    widthMm: z.number().min(10).max(190),
    fontSizePt: z.number().min(5).max(30),
    align: align.default('left'),
    bold: z.boolean().default(false),
    /** 90 = um 90° gedreht, laeuft von unten nach oben - fuer Randbeschriftungen. */
    rotate: z.union([z.literal(0), z.literal(90)]).default(0),
    /** Ohne Angabe gilt die Textfarbe der Vorlage. */
    color: hexColor.optional(),
});
export const docTemplateSchema = z.object({
    version: z.literal(1),
    page: z.object({
        marginLeftMm: marginMm,
        marginRightMm: marginMm,
        marginTopMm: marginMm,
        marginBottomMm: marginMm,
        /** Oberkante der Fließzone (Titel, Tabelle, Summen) auf der ersten Seite. */
        contentTopMm: z
            .number()
            .min(60, 'Der Inhalt kann frühestens bei 60 mm beginnen - darüber liegt der Briefkopf.')
            .max(140, 'Beginnt der Inhalt unter 140 mm, bleibt kaum Platz für Positionen.'),
    }),
    colors: z.object({
        text: hexColor,
        muted: hexColor,
        line: hexColor,
        tableHeaderBackground: hexColor,
    }),
    /** Firmenlogo aus den Firmendaten. */
    logo: z.object({
        show: z.boolean(),
        xMm,
        yMm,
        widthMm: z.number().min(10).max(80),
    }),
    /** Firmenname als Schriftzug im Briefkopf (entfaellt meist bei Logo). */
    companyName: z.object({
        show: z.boolean(),
        xMm,
        yMm,
        widthMm: z.number().min(20).max(190),
        fontSizePt: z.number().min(8).max(28),
        align: align.default('left'),
    }),
    /** Absenderzeile ueber dem Anschriftfeld (fuer Fensterumschlaege). */
    senderLine: z.object({
        show: z.boolean(),
        xMm,
        yMm,
        widthMm: z.number().min(40).max(120),
    }),
    /** Pflichtblock: Anschrift des Empfaengers. */
    addressField: z.object({
        xMm,
        yMm,
        widthMm: z.number().min(50).max(120),
    }),
    /** Pflichtblock: Belegnummer, Datum, Kundennummer usw. */
    infoBlock: z.object({
        xMm,
        yMm,
        widthMm: z.number().min(40).max(90),
        labelWidthMm: z.number().min(15).max(50),
    }),
    /** Positionstabelle: welche Kuer-Spalten erscheinen. Menge, Preis und Betrag sind Pflicht. */
    table: z.object({
        showPosition: z.boolean(),
        showItemNumber: z.boolean(),
        showDiscount: z.boolean(),
    }),
    /** Pflichtblock: Fusszeile mit den Angaben nach § 35a GmbHG. */
    footer: z.object({
        /** bottom = klassisch unten, left = vertikal am linken Seitenrand. */
        orientation: z.enum(['bottom', 'left']),
        fontSizePt: z.number().min(5).max(10),
    }),
    extraTexts: z
        .array(extraTextSchema)
        .max(20, 'Mehr als 20 Zusatztexte sind nicht vorgesehen.')
        .default([]),
});
/**
 * Der eingebaute Standard - entspricht dem bisherigen festen Layout
 * (DIN 5008 Form B). Gilt ueberall dort, wo keine eigene Vorlage
 * gespeichert ist.
 */
export const DEFAULT_DOC_TEMPLATE = {
    version: 1,
    page: { marginLeftMm: 25, marginRightMm: 20, marginTopMm: 20, marginBottomMm: 20, contentTopMm: 90 },
    colors: { text: '#1a1d23', muted: '#5d6470', line: '#c8cdd6', tableHeaderBackground: '#eef0f4' },
    logo: { show: false, xMm: 140, yMm: 15, widthMm: 45 },
    companyName: { show: true, xMm: 25, yMm: 20, widthMm: 90, fontSizePt: 13, align: 'left' },
    senderLine: { show: true, xMm: 25, yMm: 45, widthMm: 85 },
    addressField: { xMm: 25, yMm: 50, widthMm: 85 },
    infoBlock: { xMm: 120, yMm: 45, widthMm: 70, labelWidthMm: 32 },
    table: { showPosition: true, showItemNumber: true, showDiscount: true },
    footer: { orientation: 'bottom', fontSizePt: 7 },
    extraTexts: [],
};
/**
 * Felder, die in Zusatztexten als Platzhalter stehen koennen, etwa
 * "Ihr Ansprechpartner - {firma.telefon}".
 */
export const TEMPLATE_FIELDS = [
    { key: 'firma.name', label: 'Firmenname' },
    { key: 'firma.strasse', label: 'Straße der Firma' },
    { key: 'firma.plzort', label: 'PLZ und Ort der Firma' },
    { key: 'firma.telefon', label: 'Telefon' },
    { key: 'firma.email', label: 'E-Mail' },
    { key: 'firma.website', label: 'Website' },
    { key: 'firma.ustid', label: 'USt-IdNr. der Firma' },
    { key: 'firma.steuernummer', label: 'Steuernummer der Firma' },
    { key: 'firma.iban', label: 'IBAN' },
    { key: 'firma.bic', label: 'BIC' },
    { key: 'firma.bank', label: 'Bankname' },
    { key: 'beleg.art', label: 'Belegart (z. B. Rechnung)' },
    { key: 'beleg.nummer', label: 'Belegnummer' },
    { key: 'beleg.datum', label: 'Belegdatum' },
    { key: 'kunde.nummer', label: 'Kunden-/Lieferantennummer' },
    { key: 'kunde.name', label: 'Name des Empfängers' },
];
/**
 * Ersetzt Platzhalter der Form {feld} durch Werte. Unbekannte oder leere
 * Felder werden zu einer leeren Zeichenkette - ein Beleg mit "{tippfehler}"
 * im Druckbild waere peinlicher als ein fehlendes Wort.
 */
export function resolveTemplateText(text, values) {
    return text.replace(/\{([\w.]+)\}/g, (_, key) => values[key] ?? '');
}
/**
 * Prueft eine Vorlage und liefert deutsche Fehlermeldungen je Feldpfad.
 * Leeres Ergebnis heisst: gueltig.
 */
export function validateDocTemplate(candidate) {
    const parsed = docTemplateSchema.safeParse(candidate);
    if (parsed.success)
        return { ok: true, template: parsed.data };
    const errors = {};
    for (const issue of parsed.error.issues) {
        const path = issue.path.join('.') || 'vorlage';
        if (!errors[path])
            errors[path] = issue.message;
    }
    return { ok: false, errors };
}
//# sourceMappingURL=doc-template.js.map